import { useMemo, useState } from 'react';
import { Plus, Save, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { usePropertyEditorApi } from './api-context';
import { useUnsavedGuard } from '@/hooks/useUnsavedGuard';
import { extractErrorMessage } from '@/lib/error';
import { showToast } from '@/lib/toast';
import type { PcProfile, PcTeamSeat, UpsertProfileBody } from '@/services/api/property-content.api';
import {
  SectionCard,
  TextField,
  TextAreaField,
  CheckboxField,
  fieldInputClass,
  InternalBadge,
} from './fields';

/** All editable profile fields, kept as strings/bools for the form. */
interface ProfileForm {
  // General
  onsiteHours: string;
  unitsSummary: string;
  maxFloors: string;
  onsiteContactName: string;
  onsiteContactEmail: string;
  receptionPhone: string;
  managerPhone: string;
  outOfHoursPhone: string;
  managerFullName: string;
  managerDob: string;
  brandNameOnOtas: boolean;
  emergencyPhone: string;
  teamParking: string;
  maintenanceReporting: string;
  // Screening
  minGuestAge: string;
  minNightStay: string;
  childrenAllowed: boolean;
  childrenNotes: string;
  petFeeNotes: string;
  studentsOnly: boolean;
  minAdvanceBookingDays: string;
  checkInEndTime: string;
  // Operational
  checkinProcedure: string;
  checkoutProcedure: string;
  wifiSpeed: string;
  sharedFacilityAccess: string;
  fireEvacuation: string;
  rubbishRemoval: string;
  onsiteParking: string;
  nearestParking: string;
  heatingCooling: string;
  washingMachine: string;
  deliveriesLoading: string;
  // Guest FAQ
  faqOutOfHoursAccess: string;
  faqBedding: string;
  faqTowels: string;
  faqLuggageStorage: string;
  faqMail: string;
  faqBikeStorage: string;
  faqBuildingLockedHours: string;
  keyType: string;
  faqFans: string;
  buildingEntryHours: string;
  lostKeyFee: string;
  // Key mgmt (internal)
  wifiSsid: string;
  wifiUsername: string;
  wifiPassword: string;
  wifiRouterLocation: string;
  lostKeyContact: string;
  masterKeyProtocol: string;
  afterHoursLockout: string;
  afterHoursEmergencyContact: string;
}

const STRING_KEYS: (keyof ProfileForm)[] = [
  'onsiteHours', 'unitsSummary', 'onsiteContactName', 'onsiteContactEmail',
  'receptionPhone', 'managerPhone', 'outOfHoursPhone', 'managerFullName',
  'managerDob', 'emergencyPhone', 'teamParking', 'maintenanceReporting',
  'childrenNotes', 'petFeeNotes', 'checkInEndTime', 'checkinProcedure',
  'checkoutProcedure', 'wifiSpeed', 'sharedFacilityAccess', 'fireEvacuation',
  'rubbishRemoval', 'onsiteParking', 'nearestParking', 'heatingCooling',
  'washingMachine', 'deliveriesLoading', 'faqOutOfHoursAccess', 'faqBedding',
  'faqTowels', 'faqLuggageStorage', 'faqMail', 'faqBikeStorage',
  'faqBuildingLockedHours', 'keyType', 'faqFans', 'buildingEntryHours',
  'wifiSsid', 'wifiUsername', 'wifiPassword', 'wifiRouterLocation',
  'lostKeyContact', 'masterKeyProtocol', 'afterHoursLockout',
  'afterHoursEmergencyContact',
];
const INT_KEYS: (keyof ProfileForm)[] = ['maxFloors', 'minGuestAge', 'minNightStay', 'minAdvanceBookingDays'];
const BOOL_KEYS: (keyof ProfileForm)[] = ['brandNameOnOtas', 'childrenAllowed', 'studentsOnly'];

/**
 * Hotel (hideInternal) whitelist — the guest-facing keys the tenant
 * `/v1/my-property/:id/guidelines` DTO accepts (backend §3 F6 set). Every
 * internal key (WiFi creds, key/after-hours protocols, manager identity/DOB,
 * reception/emergency phones, `teamParking`, `brandNameOnOtas`,
 * `maintenanceReporting`) and `teamSeats` are omitted here, so a hotel save can
 * never carry an internal key even if such a field were somehow rendered. This
 * is the payload-level half of the two-layer guard (UI hiding is the other).
 * `checkInEndTime` (HH:MM `@Matches` on the DTO) is handled separately so an
 * empty value is skipped rather than sent as `''` and rejected.
 */
const HOTEL_STRING_KEYS: (keyof ProfileForm)[] = [
  'onsiteHours', 'unitsSummary', 'childrenNotes', 'petFeeNotes',
  'checkinProcedure', 'checkoutProcedure', 'wifiSpeed', 'sharedFacilityAccess',
  'fireEvacuation', 'rubbishRemoval', 'onsiteParking', 'nearestParking',
  'heatingCooling', 'washingMachine', 'deliveriesLoading', 'faqOutOfHoursAccess',
  'faqBedding', 'faqTowels', 'faqLuggageStorage', 'faqMail', 'faqBikeStorage',
  'faqBuildingLockedHours', 'keyType', 'faqFans', 'buildingEntryHours',
];
const HOTEL_INT_KEYS: (keyof ProfileForm)[] = ['maxFloors', 'minGuestAge', 'minNightStay', 'minAdvanceBookingDays'];
const HOTEL_BOOL_KEYS: (keyof ProfileForm)[] = ['childrenAllowed', 'studentsOnly'];

function s(v: string | null | undefined): string {
  return v ?? '';
}
function n(v: number | null | undefined): string {
  return v === null || v === undefined ? '' : String(v);
}

function toForm(p: PcProfile | null): ProfileForm {
  return {
    onsiteHours: s(p?.onsiteHours),
    unitsSummary: s(p?.unitsSummary),
    maxFloors: n(p?.maxFloors),
    onsiteContactName: s(p?.onsiteContactName),
    onsiteContactEmail: s(p?.onsiteContactEmail),
    receptionPhone: s(p?.receptionPhone),
    managerPhone: s(p?.managerPhone),
    outOfHoursPhone: s(p?.outOfHoursPhone),
    managerFullName: s(p?.managerFullName),
    managerDob: s(p?.managerDob),
    brandNameOnOtas: !!p?.brandNameOnOtas,
    emergencyPhone: s(p?.emergencyPhone),
    teamParking: s(p?.teamParking),
    maintenanceReporting: s(p?.maintenanceReporting),
    minGuestAge: n(p?.minGuestAge),
    minNightStay: n(p?.minNightStay),
    childrenAllowed: !!p?.childrenAllowed,
    childrenNotes: s(p?.childrenNotes),
    petFeeNotes: s(p?.petFeeNotes),
    studentsOnly: !!p?.studentsOnly,
    minAdvanceBookingDays: n(p?.minAdvanceBookingDays),
    checkInEndTime: s(p?.checkInEndTime),
    checkinProcedure: s(p?.checkinProcedure),
    checkoutProcedure: s(p?.checkoutProcedure),
    wifiSpeed: s(p?.wifiSpeed),
    sharedFacilityAccess: s(p?.sharedFacilityAccess),
    fireEvacuation: s(p?.fireEvacuation),
    rubbishRemoval: s(p?.rubbishRemoval),
    onsiteParking: s(p?.onsiteParking),
    nearestParking: s(p?.nearestParking),
    heatingCooling: s(p?.heatingCooling),
    washingMachine: s(p?.washingMachine),
    deliveriesLoading: s(p?.deliveriesLoading),
    faqOutOfHoursAccess: s(p?.faqOutOfHoursAccess),
    faqBedding: s(p?.faqBedding),
    faqTowels: s(p?.faqTowels),
    faqLuggageStorage: s(p?.faqLuggageStorage),
    faqMail: s(p?.faqMail),
    faqBikeStorage: s(p?.faqBikeStorage),
    faqBuildingLockedHours: s(p?.faqBuildingLockedHours),
    keyType: s(p?.keyType),
    faqFans: s(p?.faqFans),
    buildingEntryHours: s(p?.buildingEntryHours),
    lostKeyFee: s(p?.lostKeyFee),
    wifiSsid: s(p?.wifiSsid),
    wifiUsername: s(p?.wifiUsername),
    wifiPassword: s(p?.wifiPassword),
    wifiRouterLocation: s(p?.wifiRouterLocation),
    lostKeyContact: s(p?.lostKeyContact),
    masterKeyProtocol: s(p?.masterKeyProtocol),
    afterHoursLockout: s(p?.afterHoursLockout),
    afterHoursEmergencyContact: s(p?.afterHoursEmergencyContact),
  };
}

/** Hotel-safe body: only whitelisted guest-facing keys, no team seats. */
function buildHotelBody(form: ProfileForm): UpsertProfileBody {
  const body: UpsertProfileBody = {};
  for (const key of HOTEL_STRING_KEYS) {
    body[key as keyof UpsertProfileBody] = (form[key] as string).trim() as never;
  }
  // checkInEndTime is HH:MM-validated on the DTO — only send when non-empty.
  const checkInEnd = form.checkInEndTime.trim();
  if (checkInEnd !== '') body.checkInEndTime = checkInEnd;
  for (const key of HOTEL_INT_KEYS) {
    const raw = (form[key] as string).trim();
    if (raw === '') continue;
    const parsed = parseInt(raw, 10);
    if (Number.isFinite(parsed)) body[key as keyof UpsertProfileBody] = parsed as never;
  }
  for (const key of HOTEL_BOOL_KEYS) {
    body[key as keyof UpsertProfileBody] = form[key] as never;
  }
  const fee = form.lostKeyFee.trim();
  if (fee !== '') {
    const parsed = parseFloat(fee);
    if (Number.isFinite(parsed)) body.lostKeyFee = parsed;
  }
  return body;
}

function buildBody(form: ProfileForm, seats: PcTeamSeat[]): UpsertProfileBody {
  const body: UpsertProfileBody = {};
  for (const key of STRING_KEYS) {
    body[key as keyof UpsertProfileBody] = (form[key] as string).trim() as never;
  }
  for (const key of INT_KEYS) {
    const raw = (form[key] as string).trim();
    if (raw === '') continue;
    const parsed = parseInt(raw, 10);
    if (Number.isFinite(parsed)) body[key as keyof UpsertProfileBody] = parsed as never;
  }
  for (const key of BOOL_KEYS) {
    body[key as keyof UpsertProfileBody] = form[key] as never;
  }
  const fee = form.lostKeyFee.trim();
  if (fee !== '') {
    const parsed = parseFloat(fee);
    if (Number.isFinite(parsed)) body.lostKeyFee = parsed;
  }
  body.teamSeats = seats.map((seat) => ({
    name: seat.name ?? '',
    email: seat.email ?? '',
    roleLabel: seat.roleLabel ?? '',
  }));
  return body;
}

export function GuidelinesTab({
  propertyId,
  profile,
  hideInternal = false,
}: {
  propertyId: string;
  profile: PcProfile | null;
  /**
   * Hotel variant (scope #9): strips every internal field/section (contacts,
   * manager identity, WiFi/router creds, key management, after-hours, team
   * seats) AND narrows the save body to the guest-facing whitelist. Passed for
   * the hotel Listing Editor; omitted/false for the super-admin console.
   */
  hideInternal?: boolean;
}) {
  const initial = useMemo(() => toForm(profile), [profile]);
  const initialSeats = useMemo<PcTeamSeat[]>(() => profile?.teamSeats ?? [], [profile]);
  const [form, setForm] = useState<ProfileForm>(initial);
  const [seats, setSeats] = useState<PcTeamSeat[]>(initialSeats);
  const [error, setError] = useState('');
  const { useUpsertProfile } = usePropertyEditorApi();
  const mutation = useUpsertProfile(propertyId);

  const isDirty = useMemo(() => {
    const fieldsChanged = (Object.keys(form) as (keyof ProfileForm)[]).some((k) => form[k] !== initial[k]);
    const seatsChanged = JSON.stringify(seats) !== JSON.stringify(initialSeats);
    return fieldsChanged || seatsChanged;
  }, [form, initial, seats, initialSeats]);
  useUnsavedGuard(isDirty);

  const set = <K extends keyof ProfileForm>(key: K, value: ProfileForm[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  function updateSeat(idx: number, patch: Partial<PcTeamSeat>) {
    setSeats((prev) => prev.map((seat, i) => (i === idx ? { ...seat, ...patch } : seat)));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    try {
      await mutation.mutateAsync(hideInternal ? buildHotelBody(form) : buildBody(form, seats));
      showToast('Guidelines saved · live on storefront');
    } catch (err) {
      setError(extractErrorMessage(err));
      showToast('Could not save guidelines', 'error');
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700" role="alert">
          {error}
        </div>
      )}

      <SectionCard title="General">
        <TextField id="gd-onsite-hours" label="On-site team hours" value={form.onsiteHours} onChange={(v) => set('onsiteHours', v)} maxLength={255} placeholder="e.g. Reception 9–5" />
        {!hideInternal && (
          <>
            <TextField id="gd-onsite-name" label="On-site contact name" value={form.onsiteContactName} onChange={(v) => set('onsiteContactName', v)} maxLength={255} internal />
            <TextField id="gd-onsite-email" label="On-site / manager contact email" value={form.onsiteContactEmail} onChange={(v) => set('onsiteContactEmail', v)} type="email" inputMode="email" maxLength={255} internal />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <TextField id="gd-reception" label="Reception phone" value={form.receptionPhone} onChange={(v) => set('receptionPhone', v)} maxLength={50} internal />
              <TextField id="gd-manager-phone" label="Manager phone" value={form.managerPhone} onChange={(v) => set('managerPhone', v)} maxLength={50} internal />
              <TextField id="gd-ooh-phone" label="Out-of-hours phone" value={form.outOfHoursPhone} onChange={(v) => set('outOfHoursPhone', v)} maxLength={50} internal />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <TextField id="gd-manager-name" label="Property manager full name" value={form.managerFullName} onChange={(v) => set('managerFullName', v)} maxLength={255} internal />
              <TextField id="gd-manager-dob" label="Property manager DOB" value={form.managerDob} onChange={(v) => set('managerDob', v)} type="date" internal />
            </div>
          </>
        )}
      </SectionCard>

      <SectionCard title="Building & maintenance">
        <TextField id="gd-units-summary" label="Units summary" value={form.unitsSummary} onChange={(v) => set('unitsSummary', v)} maxLength={500} placeholder='e.g. "50 Studios, 21 5-BR"' />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <TextField id="gd-max-floors" label="Max floors" value={form.maxFloors} onChange={(v) => set('maxFloors', v)} type="number" inputMode="numeric" />
          {!hideInternal && (
            <TextField id="gd-emergency" label="Emergency contact number" value={form.emergencyPhone} onChange={(v) => set('emergencyPhone', v)} maxLength={50} internal />
          )}
        </div>
        {!hideInternal && (
          <>
            <TextAreaField id="gd-maintenance" label="How to report maintenance" value={form.maintenanceReporting} onChange={(v) => set('maintenanceReporting', v)} rows={3} />
            <TextAreaField id="gd-team-parking" label="Team parking" value={form.teamParking} onChange={(v) => set('teamParking', v)} rows={2} internal />
            <CheckboxField id="gd-brand-otas" label="Brand-name usage on OTAs allowed" checked={form.brandNameOnOtas} onChange={(v) => set('brandNameOnOtas', v)} internal />
          </>
        )}
      </SectionCard>

      <SectionCard title="Guest screening">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <TextField id="gd-min-age" label="Min guest age" value={form.minGuestAge} onChange={(v) => set('minGuestAge', v)} type="number" inputMode="numeric" />
          <TextField id="gd-min-night" label="Min night stay (default)" value={form.minNightStay} onChange={(v) => set('minNightStay', v)} type="number" inputMode="numeric" hint="Per-unit minimum still governs booking" />
          <TextField id="gd-min-advance" label="Min advance-booking days" value={form.minAdvanceBookingDays} onChange={(v) => set('minAdvanceBookingDays', v)} type="number" inputMode="numeric" />
          <TextField id="gd-checkin-end" label="Check-in end time" value={form.checkInEndTime} onChange={(v) => set('checkInEndTime', v)} type="time" />
        </div>
        <div className="flex flex-wrap gap-6">
          <CheckboxField id="gd-children" label="Children allowed" checked={form.childrenAllowed} onChange={(v) => set('childrenAllowed', v)} />
          <CheckboxField id="gd-students" label="Students only" checked={form.studentsOnly} onChange={(v) => set('studentsOnly', v)} />
        </div>
        <TextAreaField id="gd-children-notes" label="Children notes" value={form.childrenNotes} onChange={(v) => set('childrenNotes', v)} rows={2} />
        <TextAreaField id="gd-pet-fee-notes" label="Pet fee notes" value={form.petFeeNotes} onChange={(v) => set('petFeeNotes', v)} rows={2} />
      </SectionCard>

      <SectionCard title="Operational">
        <TextAreaField id="gd-checkin-proc" label="Check-in procedure" value={form.checkinProcedure} onChange={(v) => set('checkinProcedure', v)} rows={3} />
        <TextAreaField id="gd-checkout-proc" label="Check-out procedure" value={form.checkoutProcedure} onChange={(v) => set('checkoutProcedure', v)} rows={3} />
        <TextField id="gd-wifi-speed" label="WiFi speed" value={form.wifiSpeed} onChange={(v) => set('wifiSpeed', v)} maxLength={100} placeholder="e.g. 200 Mbps" />
        <TextAreaField id="gd-shared-facility" label="Shared-facility access & hours" value={form.sharedFacilityAccess} onChange={(v) => set('sharedFacilityAccess', v)} rows={2} />
        <TextAreaField id="gd-fire" label="Fire-alarm / evacuation procedure" value={form.fireEvacuation} onChange={(v) => set('fireEvacuation', v)} rows={2} />
        <TextAreaField id="gd-rubbish" label="Rubbish removal" value={form.rubbishRemoval} onChange={(v) => set('rubbishRemoval', v)} rows={2} />
        <TextAreaField id="gd-onsite-parking" label="On-site parking (+cost)" value={form.onsiteParking} onChange={(v) => set('onsiteParking', v)} rows={2} />
        <TextAreaField id="gd-nearest-parking" label="Nearest parking station" value={form.nearestParking} onChange={(v) => set('nearestParking', v)} rows={2} />
        <TextAreaField id="gd-heating" label="Heating / AC" value={form.heatingCooling} onChange={(v) => set('heatingCooling', v)} rows={2} />
        <TextAreaField id="gd-washing" label="Washing machine" value={form.washingMachine} onChange={(v) => set('washingMachine', v)} rows={2} />
        <TextAreaField id="gd-deliveries" label="Deliveries / loading bay" value={form.deliveriesLoading} onChange={(v) => set('deliveriesLoading', v)} rows={2} />
      </SectionCard>

      <SectionCard title="Guest FAQ">
        <TextAreaField id="gd-faq-ooh" label="Out-of-hours check-in/out" value={form.faqOutOfHoursAccess} onChange={(v) => set('faqOutOfHoursAccess', v)} rows={2} />
        <TextAreaField id="gd-faq-bedding" label="Pillows / bedding" value={form.faqBedding} onChange={(v) => set('faqBedding', v)} rows={2} />
        <TextAreaField id="gd-faq-towels" label="Towels" value={form.faqTowels} onChange={(v) => set('faqTowels', v)} rows={2} />
        <TextAreaField id="gd-faq-luggage" label="Luggage storage" value={form.faqLuggageStorage} onChange={(v) => set('faqLuggageStorage', v)} rows={2} />
        <TextAreaField id="gd-faq-mail" label="Mail delivery" value={form.faqMail} onChange={(v) => set('faqMail', v)} rows={2} />
        <TextAreaField id="gd-faq-bike" label="Bike storage" value={form.faqBikeStorage} onChange={(v) => set('faqBikeStorage', v)} rows={2} />
        <TextAreaField id="gd-faq-locked" label="Building locked hours" value={form.faqBuildingLockedHours} onChange={(v) => set('faqBuildingLockedHours', v)} rows={2} />
        <TextAreaField id="gd-faq-fans" label="Fans on request" value={form.faqFans} onChange={(v) => set('faqFans', v)} rows={2} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TextField id="gd-key-type" label="Key type (fob / physical)" value={form.keyType} onChange={(v) => set('keyType', v)} maxLength={50} />
          <TextField id="gd-building-hours" label="Building entry hours" value={form.buildingEntryHours} onChange={(v) => set('buildingEntryHours', v)} maxLength={50} placeholder="e.g. 06:00–23:00" />
        </div>
        {hideInternal && (
          <TextField id="gd-lost-key-fee" label="Lost-key fee" value={form.lostKeyFee} onChange={(v) => set('lostKeyFee', v)} inputMode="decimal" hint="Advertised charge — shown to guests" className="max-w-[200px]" />
        )}
      </SectionCard>

      {!hideInternal && (
        <SectionCard title="Key management & after-hours" internal description="Security-sensitive — never shown on the storefront (except lost-key fee).">
          <TextField id="gd-lost-key-fee" label="Lost-key fee" value={form.lostKeyFee} onChange={(v) => set('lostKeyFee', v)} inputMode="decimal" hint="Advertised charge — shown to guests" className="max-w-[200px]" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <TextField id="gd-wifi-ssid" label="WiFi network name (SSID)" value={form.wifiSsid} onChange={(v) => set('wifiSsid', v)} maxLength={255} internal />
            <TextField id="gd-wifi-user" label="WiFi username" value={form.wifiUsername} onChange={(v) => set('wifiUsername', v)} maxLength={255} internal />
            <TextField id="gd-wifi-pass" label="WiFi password" value={form.wifiPassword} onChange={(v) => set('wifiPassword', v)} maxLength={255} internal />
            <TextField id="gd-wifi-router" label="WiFi router location" value={form.wifiRouterLocation} onChange={(v) => set('wifiRouterLocation', v)} maxLength={255} internal />
          </div>
          <TextAreaField id="gd-lost-key-contact" label="Lost-key contact & charge" value={form.lostKeyContact} onChange={(v) => set('lostKeyContact', v)} rows={2} internal />
          <TextAreaField id="gd-master-key" label="Master-key access / malfunction protocol" value={form.masterKeyProtocol} onChange={(v) => set('masterKeyProtocol', v)} rows={2} internal />
          <TextAreaField id="gd-afterhours-lockout" label="After-hours lockout procedure" value={form.afterHoursLockout} onChange={(v) => set('afterHoursLockout', v)} rows={2} internal />
          <TextField id="gd-afterhours-contact" label="Off-hours emergency contact" value={form.afterHoursEmergencyContact} onChange={(v) => set('afterHoursEmergencyContact', v)} maxLength={255} internal />
        </SectionCard>
      )}

      {!hideInternal && (
        <SectionCard title="Team seats" internal description="Staff seats captured from onboarding. Role label is free text, not a system role.">
          {seats.length === 0 ? (
            <p className="text-sm text-gray-400">No team seats recorded.</p>
          ) : (
            <div className="space-y-3">
              {seats.map((seat, idx) => (
                <div key={idx} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_1fr_auto] gap-2 items-center">
                  <input aria-label={`Seat ${idx + 1} name`} className={fieldInputClass} placeholder="Name" value={seat.name} onChange={(e) => updateSeat(idx, { name: e.target.value })} />
                  <input aria-label={`Seat ${idx + 1} email`} className={fieldInputClass} placeholder="Email" value={seat.email} onChange={(e) => updateSeat(idx, { email: e.target.value })} />
                  <input aria-label={`Seat ${idx + 1} role label`} className={fieldInputClass} placeholder="Role label" value={seat.roleLabel} onChange={(e) => updateSeat(idx, { roleLabel: e.target.value })} />
                  <button type="button" onClick={() => setSeats((prev) => prev.filter((_, i) => i !== idx))} className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50" aria-label={`Remove seat ${idx + 1}`}>
                    <Trash2 size={16} aria-hidden="true" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <Button type="button" variant="secondary" size="sm" onClick={() => setSeats((prev) => [...prev, { name: '', email: '', roleLabel: '' }])}>
            <Plus size={14} aria-hidden="true" />
            Add seat
          </Button>
        </SectionCard>
      )}

      <div className="sticky bottom-0 flex items-center justify-between gap-3 border-t border-gray-200 bg-white/90 py-3 backdrop-blur">
        <span className="flex items-center gap-2 text-sm text-gray-500" aria-live="polite">
          {!hideInternal && <InternalBadge />}
          {isDirty ? 'You have unsaved changes.' : 'All changes saved.'}
        </span>
        <Button type="submit" isLoading={mutation.isPending} disabled={!isDirty || mutation.isPending}>
          <Save size={16} aria-hidden="true" />
          Save guidelines
        </Button>
      </div>
    </form>
  );
}
